import React, { useState, useEffect } from 'react';
import { API_BASE_URL, getApiHeaders } from '../config/api';
import './ProfiloPage.css';

const ProfiloPage = () => {
  // Stati per ogni sezione
  const [familyMembers, setFamilyMembers] = useState([]);
  const [healthInfo, setHealthInfo] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [importantDates, setImportantDates] = useState([]);
  const [dailyRoutine, setDailyRoutine] = useState([]);

  // Stati per sezioni espanse
  const [expandedSections, setExpandedSections] = useState({});

  // Stati per dialog modali
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    await loadFamilyMembers();
    await loadHealthInfo();
    await loadPreferences();
    await loadEmergencyContacts();
    await loadImportantDates();
    await loadDailyRoutine();
  };

  const loadFamilyMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/family-members`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setFamilyMembers(data.data);
    } catch (error) {
      console.error('Error loading family members:', error);
    }
  };

  const loadHealthInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/health-info`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setHealthInfo(data.data);
    } catch (error) {
      console.error('Error loading health info:', error);
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/user-preferences`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setPreferences(data.data);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadEmergencyContacts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/emergency-contacts`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setEmergencyContacts(data.data);
    } catch (error) {
      console.error('Error loading emergency contacts:', error);
    }
  };

  const loadImportantDates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/important-dates`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setImportantDates(data.data);
    } catch (error) {
      console.error('Error loading important dates:', error);
    }
  };

  const loadDailyRoutine = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/daily-routine`, {
        headers: getApiHeaders()
      });
      const data = await response.json();
      if (data.success) setDailyRoutine(data.data);
    } catch (error) {
      console.error('Error loading daily routine:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openAddDialog = (type) => {
    setDialogType(type);
    setEditingItem(null);
    setFormData({});
    setShowDialog(true);
  };

  const openEditDialog = (type, item) => {
    setDialogType(type);
    setEditingItem(item);
    setFormData(item);
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const endpoint = dialogType;
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem
        ? `${API_BASE_URL}/api/profilo/${endpoint}/${editingItem.id}`
        : `${API_BASE_URL}/api/profilo/${endpoint}`;

      const response = await fetch(url, {
        method,
        headers: getApiHeaders(),
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowDialog(false);
        loadAllData();
        alert(editingItem ? 'Modificato con successo!' : 'Aggiunto con successo!');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo elemento?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/profilo/${type}/${id}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });

      const data = await response.json();
      if (data.success) {
        loadAllData();
        alert('Eliminato con successo!');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="profilo-page">
      <div className="page-header">
        <h1>Profilo Personale</h1>
        <p className="subtitle">Gestisci tutte le informazioni importanti</p>
      </div>

      {/* SEZIONE FAMIGLIA */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('family')}>
          <h2>Famiglia</h2>
          <button className="btn-expand">{expandedSections.family ? '−' : '+'}</button>
        </div>
        {expandedSections.family && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('family-members')}>
              + Aggiungi Familiare
            </button>
            <div className="items-list">
              {familyMembers.map(member => (
                <div key={member.id} className="item-card">
                  <div className="item-info">
                    <h3>{member.name}</h3>
                    <p>{member.relation}</p>
                    {member.birthDate && <p>Data di nascita: {member.birthDate}</p>}
                    {member.notes && <p className="notes">{member.notes}</p>}
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('family-members', member)}>Modifica</button>
                    <button onClick={() => handleDelete('family-members', member.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {familyMembers.length === 0 && (
                <div className="empty-state">Nessun familiare aggiunto</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE SALUTE */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('health')}>
          <h2>Salute e Medicinali</h2>
          <button className="btn-expand">{expandedSections.health ? '−' : '+'}</button>
        </div>
        {expandedSections.health && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('health-info')}>
              + Aggiungi Medicinale
            </button>
            <div className="items-list">
              {healthInfo.map(med => (
                <div key={med.id} className="item-card">
                  <div className="item-info">
                    <h3>{med.medicine_name}</h3>
                    {med.dosage && <p>Dosaggio: {med.dosage}</p>}
                    {med.schedule && <p>Orario: {med.schedule}</p>}
                    {med.notes && <p className="notes">{med.notes}</p>}
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('health-info', med)}>Modifica</button>
                    <button onClick={() => handleDelete('health-info', med.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {healthInfo.length === 0 && (
                <div className="empty-state">Nessun medicinale aggiunto</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE PREFERENZE */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('preferences')}>
          <h2>Preferenze</h2>
          <button className="btn-expand">{expandedSections.preferences ? '−' : '+'}</button>
        </div>
        {expandedSections.preferences && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('user-preferences')}>
              + Aggiungi Preferenza
            </button>
            <div className="items-list">
              {preferences.map(pref => (
                <div key={pref.id} className="item-card">
                  <div className="item-info">
                    <h3>{pref.category}</h3>
                    <p>{pref.value}</p>
                    {pref.notes && <p className="notes">{pref.notes}</p>}
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('user-preferences', pref)}>Modifica</button>
                    <button onClick={() => handleDelete('user-preferences', pref.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {preferences.length === 0 && (
                <div className="empty-state">Nessuna preferenza aggiunta</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE CONTATTI EMERGENZA */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('emergency')}>
          <h2>Contatti di Emergenza</h2>
          <button className="btn-expand">{expandedSections.emergency ? '−' : '+'}</button>
        </div>
        {expandedSections.emergency && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('emergency-contacts')}>
              + Aggiungi Contatto
            </button>
            <div className="items-list">
              {emergencyContacts.map(contact => (
                <div key={contact.id} className="item-card">
                  <div className="item-info">
                    <h3>{contact.name}</h3>
                    <p>{contact.relation}</p>
                    <p className="phone">{contact.phone}</p>
                    <p>Priorita: {contact.priority}</p>
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('emergency-contacts', contact)}>Modifica</button>
                    <button onClick={() => handleDelete('emergency-contacts', contact.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {emergencyContacts.length === 0 && (
                <div className="empty-state">Nessun contatto di emergenza aggiunto</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE DATE IMPORTANTI */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('dates')}>
          <h2>Date Importanti</h2>
          <button className="btn-expand">{expandedSections.dates ? '−' : '+'}</button>
        </div>
        {expandedSections.dates && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('important-dates')}>
              + Aggiungi Data
            </button>
            <div className="items-list">
              {importantDates.map(date => (
                <div key={date.id} className="item-card">
                  <div className="item-info">
                    <h3>{date.description}</h3>
                    <p>Data: {date.date}</p>
                    <p>{date.recurring ? 'Ricorrente' : 'Una tantum'}</p>
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('important-dates', date)}>Modifica</button>
                    <button onClick={() => handleDelete('important-dates', date.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {importantDates.length === 0 && (
                <div className="empty-state">Nessuna data importante aggiunta</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE ROUTINE GIORNALIERA */}
      <div className="profile-section">
        <div className="section-header" onClick={() => toggleSection('routine')}>
          <h2>Routine Giornaliera</h2>
          <button className="btn-expand">{expandedSections.routine ? '−' : '+'}</button>
        </div>
        {expandedSections.routine && (
          <div className="section-content">
            <button className="btn-add" onClick={() => openAddDialog('daily-routine')}>
              + Aggiungi Attivita
            </button>
            <div className="items-list">
              {dailyRoutine.map(activity => (
                <div key={activity.id} className="item-card">
                  <div className="item-info">
                    <h3>{activity.time}</h3>
                    <p>{activity.activity}</p>
                    {activity.notes && <p className="notes">{activity.notes}</p>}
                  </div>
                  <div className="item-actions">
                    <button onClick={() => openEditDialog('daily-routine', activity)}>Modifica</button>
                    <button onClick={() => handleDelete('daily-routine', activity.id)}>Elimina</button>
                  </div>
                </div>
              ))}
              {dailyRoutine.length === 0 && (
                <div className="empty-state">Nessuna attivita aggiunta</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL DIALOG */}
      {showDialog && (
        <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{editingItem ? 'Modifica' : 'Aggiungi'}</h3>
              <button className="dialog-close" onClick={() => setShowDialog(false)}>×</button>
            </div>
            <div className="dialog-body">
              {/* Render form fields based on dialogType */}
              {dialogType === 'family-members' && (
                <>
                  <input
                    type="text"
                    placeholder="Nome"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Relazione (es. Figlia, Figlio)"
                    value={formData.relation || ''}
                    onChange={(e) => setFormData({...formData, relation: e.target.value})}
                  />
                  <input
                    type="date"
                    placeholder="Data di nascita"
                    value={formData.birthDate || ''}
                    onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                  />
                  <textarea
                    placeholder="Note"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </>
              )}

              {dialogType === 'health-info' && (
                <>
                  <input
                    type="text"
                    placeholder="Nome medicinale"
                    value={formData.medicine_name || ''}
                    onChange={(e) => setFormData({...formData, medicine_name: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Dosaggio"
                    value={formData.dosage || ''}
                    onChange={(e) => setFormData({...formData, dosage: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Orario (es. 08:00, 20:00)"
                    value={formData.schedule || ''}
                    onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                  />
                  <textarea
                    placeholder="Note"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </>
              )}

              {dialogType === 'user-preferences' && (
                <>
                  <input
                    type="text"
                    placeholder="Categoria (es. Cibo, Musica, Colore)"
                    value={formData.category || ''}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Valore"
                    value={formData.value || ''}
                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                  />
                  <textarea
                    placeholder="Note"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </>
              )}

              {dialogType === 'emergency-contacts' && (
                <>
                  <input
                    type="text"
                    placeholder="Nome"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Relazione"
                    value={formData.relation || ''}
                    onChange={(e) => setFormData({...formData, relation: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Telefono"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                  <input
                    type="number"
                    placeholder="Priorita (1-5)"
                    min="1"
                    max="5"
                    value={formData.priority || 1}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                  />
                </>
              )}

              {dialogType === 'important-dates' && (
                <>
                  <input
                    type="date"
                    value={formData.date || ''}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Descrizione"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.recurring || false}
                      onChange={(e) => setFormData({...formData, recurring: e.target.checked ? 1 : 0})}
                    />
                    Ricorrente
                  </label>
                </>
              )}

              {dialogType === 'daily-routine' && (
                <>
                  <input
                    type="time"
                    value={formData.time || ''}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Attivita"
                    value={formData.activity || ''}
                    onChange={(e) => setFormData({...formData, activity: e.target.value})}
                  />
                  <textarea
                    placeholder="Note"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </>
              )}
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowDialog(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave}>Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfiloPage;
